import React from 'react';
import { FormGroup } from 'reactstrap';
import { faPhoneVolume } from '@fortawesome/free-solid-svg-icons';
import { httpClient } from '@s20/services/httpClient';
import { BaseFormViewModel } from '../common/ViewModel';
import {
    FormLabel,
    onFieldChange,
    toasty,
    Input,
    InfoCheckbox,
} from '../common/forms/FormElements';
import CommonContext, { ApiRoutes } from '../Common';
import SlideForm from '../common/forms/SlideForm';
import { handleFormSaveError } from '../common/forms/ValidationError';
import ValidatedSelect from '../common/forms/ValidatedSelect';

class CallDispositionModel {
    id = 0;

    tenantId = null;

    description = '';

    notesRequired = false;

    isActive = true;

    directions = [];
}

export default class CallDispositionForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();

        this.state = {
            disposition: null,
            show: false,
            directions: props.directions || [],
            readOnly: false,
            removeUnavailable: true,
            ...new BaseFormViewModel(),
        };

        this.onSubmit = this.onSubmit.bind(this);
        this.onDelete = this.onDelete.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onChange = this.onChange.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    onChange = onFieldChange;

    onClose = () => {
        this.resetForm();
        this.setState({ show: false, disposition: null });
        this.context.setFormOpened(false);
    };

    onDelete = async () => {
        this.setState({ readOnly: true });

        const { disposition } = this.state;

        const response = await httpClient
            .delete(ApiRoutes.callDisposition.delete(disposition.id))
            .catch(this.handleSaveError);

        if (response) {
            toasty.success('Disposition Deleted', 'Disposition successfully deleted.');
            this.setState({ show: false, disposition: null });
            this.context.setFormOpened(false);
            if (this.props.onSaved) {
                this.props.onSaved();
            }
        }

        this.setState({ readOnly: false });
    };

    onSubmit = async () => {
        this.setState({ readOnly: true });

        const { disposition } = this.state;

        let savedSuccessfully = false;

        if (disposition.id && disposition.id > 0) {
            savedSuccessfully = await this.updateDisposition(disposition);

            if (savedSuccessfully) {
                toasty.success('Disposition Updated', 'Disposition successfully updated.');
            }
        } else {
            savedSuccessfully = await this.createDisposition(disposition);

            if (savedSuccessfully) {
                toasty.success('Disposition Created', 'Disposition successfully created.');
            }
        }

        this.setState({ readOnly: false });

        if (!savedSuccessfully) {
            return;
        }

        this.clearErrors();
        this.setState({ show: false, disposition: null });
        this.context.setFormOpened(false);

        if (this.props.onSaved) {
            this.props.onSaved();
        }

        this.resetForm();
    };

    clearErrors = () => this.setState({ errors: {} });

    createDisposition = async (disposition) => {
        const createResponse = await httpClient
            .post(ApiRoutes.callDisposition.create(), disposition)
            .catch(this.handleSaveError);

        return Boolean(createResponse);
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    open = async (id) => {
        this.setState({ loading: true });

        const [disposition] = await Promise.all([
            httpClient.js(ApiRoutes.callDisposition.byId(id)),
        ]);

        if (!disposition) {
            toasty.error(
                'Call Disposition',
                'We failed to find that disposition. Please refresh and try again.',
            );

            this.setState({ loading: false });
            return;
        }

        this.context.setFormOpened(true);
        this.setState({
            show: true,
            loading: false,
            disposition,
        });
    };

    openNew = () => {
        const disposition = new CallDispositionModel();

        this.context.setFormOpened(true);
        this.setState({ disposition, show: true });
    };

    populateState = async () => {
        let { directions } = this.state;

        const removeUnavailable = !this.context.permissions.find(
            (p) => p.permission === 'call_disposition' && p.action === 'delete',
        );

        if (!directions || !directions.length) {
            directions = await httpClient.js(ApiRoutes.callDisposition.directions());
            this.setState({ directions });
        }

        this.setState({ loading: false, removeUnavailable });
    };

    resetForm = () => this.setState({ formValidated: false });

    updateDisposition = async (disposition) => {
        const updateResponse = await httpClient
            .put(ApiRoutes.callDisposition.update(disposition.id), disposition)
            .catch(this.handleSaveError);

        return Boolean(updateResponse);
    };

    render() {
        const {
            disposition,
            formValidated,
            validationMessage,
            loading,
            show,
            directions,
            readOnly,
            removeUnavailable,
        } = this.state;

        if (!disposition) {
            return '';
        }

        const isNew = !disposition.id || disposition.id === 0;
        const titlePrefix = isNew ? 'Add' : 'Edit';

        const directionOptions = directions.map((d) => ({
            value: d.id,
            label: d.description,
        }));

        const selectedDirections = (disposition.directions || []).map((d) => ({
            value: d.id,
            label: d.description,
        }));

        return (
            <SlideForm
                loading={loading}
                show={show}
                id="CallDispositionForm"
                formIcon={faPhoneVolume}
                formTitle={`${titlePrefix} Call Disposition`}
                ref={this.formRef}
                setIsValidated={(value) => {
                    this.setState({ formValidated: value });
                }}
                isValidated={formValidated}
                onSubmit={this.onSubmit}
                onClose={this.onClose}
                onDelete={this.onDelete}
                errors={this.state.errors}
                onClearErrors={this.clearErrors}
                error={validationMessage}
                entityId={disposition.id}
                readOnly={readOnly}
                removeUnavailable={removeUnavailable}
            >
                <Input
                    label="Description"
                    id="description"
                    name="disposition.description"
                    value={disposition.description ?? ''}
                    onChange={this.onChange}
                    placeholder="Enter description (maximum 100 characters)"
                    maxLength="100"
                    error="Description is required."
                    required
                />

                <FormGroup>
                    <FormLabel
                        htmlFor="directions"
                        text="Directions"
                        required
                    />
                    <ValidatedSelect
                        id="directions"
                        name="disposition.directions"
                        required
                        isMulti
                        options={directionOptions}
                        value={selectedDirections}
                        onChange={(selected) => {
                            const { disposition: d } = this.state;
                            d.directions = (selected || []).map((s) => ({
                                id: s.value,
                                description: s.label,
                            }));
                            this.setState({ disposition: d });
                        }}
                        error="Please select at least one direction."
                    />
                </FormGroup>

                <InfoCheckbox
                    label="Notes Required"
                    id="notesRequired"
                    name="disposition.notesRequired"
                    checked={disposition.notesRequired}
                    onChange={this.onChange}
                />

                <InfoCheckbox
                    label="Active"
                    id="isActive"
                    name="disposition.isActive"
                    checked={disposition.isActive}
                    onChange={this.onChange}
                />
            </SlideForm>
        );
    }
}
